import { useFormik } from 'formik';
import { TFunction } from 'i18next';
import { MarkObj } from 'rc-slider/lib/Marks';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { LEVERAGE, POSITION_SIDE, TRADE_TYPE } from 'src/common/constants';
import { isEmptyObj } from 'src/common/helpers';
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Range,
    Input,
} from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { getLeverage, setLeverage } from 'src/pages/derivatives/_redux/derivativePageActions';
import { FutureSymbolSettings } from 'src/types';

interface LeverageModalProps {
    open: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { colors, typography, breakpoints } }) => `
        .modal-paper {
            width: 424px;
            border-radius: 10px;
            padding: 20px 0 30px;
            @media (min-width: ${breakpoints.md}) {
                width: 424px;
            }
        }
        .modal-title {
            padding: 0 24px;
            > div {
                font-size: ${typography.size.desktop.paragraph.p1};
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                @media (max-width: ${breakpoints.md}) {
                    font-size: ${typography.size.desktop.paragraph.p2};
                }
            }
        }
        .modal-content {
            padding: 32px 24px;
            .input-container {
                margin-bottom: 0px;
            }
            .value-up-down {
                span {
                    &:nth-of-type(1) {
                        padding-right: 10px;
                    }
                    &:nth-of-type(3) {
                        padding-left: 10px;
                    }
                }
            }
        }
        .modal-actions {
            padding: 0 24px;
            gap: 16px;
            justify-content: space-between;
            button {
                line-height: ${typography.lineHeight[4]};
                font-weight: ${typography.weight.medium};
                &:nth-of-type(2) {
                    color: ${colors.text.tertiary};
                }
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        color: ${colors.text.secondary};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        font-weight: ${typography.weight.normal};
    `}
`;

const SubTitleText = styled.span`
    ${({ theme: { typography, colors } }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        color: ${colors.text.primary};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        font-weight: ${typography.weight.normal};
    `}
`;

const DescriptionText = styled.p`
    ${({ theme: { typography, colors } }) => `
        padding: 10px;
        border-radius: 4px;
        background-color: ${colors.background.secondary.v3};
        font-size: ${typography.size.desktop.paragraph.p3}; 
        color: ${colors.text.tertiary};
        line-height: ${typography.lineHeight[2]};
        font-weight: ${typography.weight.normal};
    `}
`;

const TitleText = styled.div`
    ${({ theme: { colors } }) => `
        color: ${colors.text.primary};
        display: inline-block;
    `}
`;

const StyledInput = styled(Input)`
    ${({ theme: { colors } }) => `
        & input:disabled {
            color: ${colors.text.disabled};
        }
    `};
`;

const StyledSpan = styled.span<{ color?: string; disabled?: boolean }>`
    ${({ theme: { colors }, color }) => `
        color: ${color ? color : colors.text.primary};
        cursor: pointer;
    `}
`;

const InfoMessage = styled(FlexContainer)`
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.mobile.paragraph.p3};
        font-weight: ${typography.weight.normal};
        line-height: ${typography.lineHeight[2]};
        color: ${colors.alert.yellow};
        margin-top: 16px;
    `}
`;

interface StepsData {
    tickSizes: number[];
    marksValues: number[];
}

interface StepsDataMap {
    [key: number]: StepsData;
}

const stepValues: number[] = [1, 2, 3, 4, 5, 6, 7];

const stepsData: StepsDataMap = {
    10: {
        tickSizes: [1, 1, 1, 1, 1, 2, 3],
        marksValues: [1, 2, 3, 4, 5, 7, 10],
    },

    25: {
        tickSizes: [2, 2, 2, 5, 5, 5, 5],
        marksValues: [1, 3, 5, 10, 15, 20, 25],
    },

    50: {
        tickSizes: [2, 2, 2, 5, 5, 10, 25],
        marksValues: [1, 3, 5, 10, 15, 25, 50],
    },
    100: {
        tickSizes: [2, 2, 2, 5, 15, 25, 50],
        marksValues: [1, 3, 5, 10, 25, 50, 100],
    },
};

const possibleLeverages: number[] = [100, 50, 25, 10];

const validationSchema = (
    shortMaxLeverage: number,
    longMaxLeverage: number,
    t: TFunction<'translation', undefined>,
) => {
    return Yup.object().shape({
        long: Yup.number()
            .min(
                1,
                t('form_validation.cannot_less', {
                    field: t('leverage'),
                    length: '1',
                }),
            )
            .max(
                longMaxLeverage,
                t('form_validation.cannot_greater', {
                    field: t('leverage'),
                    length: `${longMaxLeverage}x`,
                }),
            )
            .required(t('form_validation.field_required', { field: t('leverage') })),
        short: Yup.number()
            .min(
                1,
                t('form_validation.cannot_less', {
                    field: t('leverage'),
                    length: '1',
                }),
            )
            .max(
                shortMaxLeverage,
                t('form_validation.cannot_greater', {
                    field: t('leverage'),
                    length: `${shortMaxLeverage}x`,
                }),
            )
            .required(t('form_validation.field_required', { field: t('leverage') })),
    });
};

const LeverageModal: FC<LeverageModalProps> = ({ open = true, handleClose }) => {
    const { colors } = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [maxLongLeverage, setMaxLongLeverage] = useState<number>(10);
    const [maxShortLeverage, setMaxShortLeverage] = useState<number>(10);
    const [longSliderValue, setLongSliderValue] = useState<number>(1);
    const [shortSliderValue, setShortSliderValue] = useState<number>(1);
    const [longLeverage, setLongLeverage] = useState<string>('1');
    const [shortLeverage, setShortLeverage] = useState<string>('1');
    const [isLongLeverageDisabled] = useState<boolean>(false);
    const [isShortLeverageDisabled] = useState<boolean>(false);
    const [longLeverageMarks, setLongLeverageMarks] = useState<
        Record<string | number, ReactNode | MarkObj>
    >({});
    const [shortLeverageMarks, setShortLeverageMarks] = useState<
        Record<string | number, ReactNode | MarkObj>
    >({});
    const currentSymbolSettings: FutureSymbolSettings = useAppSelector(
        (state) => state.derivativePage.symbolSettings,
    );
    const { riskLimit, symbolId } = currentSymbolSettings;
    const { leverage } = useAppSelector((state) => state.derivativePage);

    useEffect(() => {
        handleInputChange(longLeverage, LEVERAGE.LONG);
        handleInputChange(shortLeverage, LEVERAGE.SHORT);
    }, [maxLongLeverage, maxShortLeverage]);

    useEffect(() => {
        riskLimit.map((item) => {
            const leverage = 1 / Number(item.initialMargin);
            const maxLeverage: number = Number(
                possibleLeverages[possibleLeverages.findIndex((val) => Number(val) <= leverage)],
            );
            if (item.side === TRADE_TYPE.SELL_OPEN) {
                setMaxShortLeverage(maxLeverage);
                setShortLeverageMarks(generateMarks(maxLeverage));
            }
            if (item.side === TRADE_TYPE.BUY_OPEN) {
                setMaxLongLeverage(maxLeverage);
                setLongLeverageMarks(generateMarks(maxLeverage));
            }
        });
    }, [riskLimit]);

    useEffect(() => {
        if (!isEmptyObj(leverage)) {
            setLongLeverage(leverage?.leverageLong.toString());
            handleInputChange(leverage?.leverageLong.toString(), LEVERAGE.LONG);
            setShortLeverage(leverage?.leverageShort.toString());
            handleInputChange(leverage?.leverageShort.toString(), LEVERAGE.SHORT);
        }
    }, [leverage, symbolId]);

    const generateMarks = (maxLeverage: number): Record<string | number, ReactNode | MarkObj> => {
        const marks: Record<string | number, ReactNode | MarkObj> = {};
        let stepsInfo = stepsData[maxLeverage];
        for (let i = 0; i < stepValues.length; i++) {
            marks[stepValues[i]] = `${stepsInfo.marksValues[i]}x`;
        }
        return marks;
    };

    const calculateInputValue = (inputValue: number, leverageType: string) => {
        const stepsInfo =
            stepsData[leverageType === LEVERAGE.LONG ? maxLongLeverage : maxShortLeverage];
        const stepIndex = stepsInfo.marksValues.findIndex((val) => inputValue <= val);
        const lowerStepValue = stepsInfo.marksValues[stepIndex - 1] || 1;
        const upperStepValue = stepsInfo.marksValues[stepIndex];
        let sliderValue = stepValues[stepIndex - 1] || 1;
        const proportion = (inputValue - lowerStepValue) / (upperStepValue - lowerStepValue);
        if (proportion) {
            sliderValue += proportion;
        }
        return sliderValue;
    };

    const handleInputChange = (value: string, leverageType: string) => {
        const regex = new RegExp(`^\\d+\\.?\\d{0,1}$`);
        if (value && !regex.test(value)) {
            return;
        }
        const sliderValueToSet = calculateInputValue(Number(value), leverageType);
        setFieldValue(leverageType, value);

        if (leverageType === LEVERAGE.LONG) {
            setLongSliderValue(sliderValueToSet);
            setLongLeverage(value);
        } else {
            setShortSliderValue(sliderValueToSet);
            setShortLeverage(value);
        }
    };

    const handleIncrementDecrementValue = (type: string, value: number) => {
        let calcValue = Number(Math.abs(0.1 + value).toFixed(1));
        if (calcValue === Infinity) {
            calcValue = 1;
        }
        if (calcValue >= 1 && calcValue <= stepValues[6]) {
            handleSliderChange(calcValue, type);
        }
    };

    const handleSliderChange = (value: number, name: string) => {
        let stepsInfo = stepsData[name === LEVERAGE.LONG ? maxLongLeverage : maxShortLeverage];
        let decimalValue = value % 1;
        const stepIndex = stepValues.findIndex((val) => value <= val);

        const tickSize = stepsInfo.tickSizes[stepIndex] || 1;
        let calValue = decimalValue * tickSize;

        if (decimalValue !== 0) {
            calValue += stepsInfo.marksValues[stepIndex - 1];
        } else {
            calValue = stepsInfo.marksValues[stepIndex];
        }
        if (name === LEVERAGE.LONG) {
            setLongSliderValue(value);
            setLongLeverage(calValue.toFixed(1));
        } else {
            setShortSliderValue(value);
            setShortLeverage(calValue.toFixed(1));
        }
        setFieldValue(name, calValue.toFixed(1));
    };

    const { setFieldValue, errors, handleSubmit } = useFormik({
        initialValues: {
            long: longLeverage,
            short: shortLeverage,
        },
        validationSchema: validationSchema(maxShortLeverage, maxLongLeverage, t),
        validateOnMount: false,
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: (values) => {
            const leverageLong = Number(Number(leverage.leverageLong).toFixed(1));
            const leverageShort = Number(Number(leverage.leverageShort).toFixed(1));
            if (leverageLong !== Number(values.long)) {
                dispatch(
                    setLeverage({
                        symbol_id: symbolId,
                        leverage: values.long,
                        is_long: Number(POSITION_SIDE.LONG),
                    }),
                );
            }
            if (leverageShort !== Number(values.short)) {
                dispatch(
                    setLeverage({
                        symbol_id: symbolId,
                        leverage: values.short,
                        is_long: Number(POSITION_SIDE.SHORT),
                    }),
                );
            }
            setTimeout(() => {
                dispatch(
                    getLeverage({
                        symbol_id: symbolId,
                    }),
                );
            }, 1000);
            handleClose();
        },
    });

    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <TitleText>{t('adjust_leverage')}</TitleText>
                <Cross
                    fill={colors.text.secondary}
                    onClick={handleClose}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <FlexContainer
                    styles={{ flexDirection: 'column', width: '100%', alignItems: 'start' }}
                >
                    <FlexContainer
                        styles={{
                            width: '100%',
                            gap: 12,
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            marginBottom: 16,
                        }}
                    >
                        <SubTitleText>{t('isolated_margin')}</SubTitleText>
                        <DescriptionText>{t('isolated_margin_description')}</DescriptionText>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            gap: 16,
                            flexDirection: 'column',
                            marginBottom: 32,
                        }}
                    >
                        <FlexContainer
                            styles={{
                                flexDirection: 'column',
                                width: '100%',
                                alignItems: 'flex-start',
                                gap: 8,
                            }}
                        >
                            <LabelText>{t('leverage_side', { side: t(LEVERAGE.LONG) })}</LabelText>
                            <StyledInput
                                name="long"
                                hasTooltip={false}
                                value={longLeverage}
                                // disabled={isLongLeverageDisabled}
                                fullWidth
                                error={errors.long}
                                rightElement={
                                    <FlexContainer
                                        className="value-up-down"
                                        styles={{
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '39px',
                                        }}
                                    >
                                        <StyledSpan
                                            disabled={isLongLeverageDisabled}
                                            onClick={() => {
                                                // !isLongLeverageDisabled &&
                                                handleIncrementDecrementValue(
                                                    LEVERAGE.LONG,
                                                    -longSliderValue,
                                                );
                                            }}
                                        >
                                            -
                                        </StyledSpan>
                                        <StyledSpan color={colors.text.tertiary}>|</StyledSpan>
                                        <StyledSpan
                                            disabled={isLongLeverageDisabled}
                                            onClick={() => {
                                                // !isLongLeverageDisabled &&
                                                handleIncrementDecrementValue(
                                                    LEVERAGE.LONG,
                                                    longSliderValue,
                                                );
                                            }}
                                        >
                                            +
                                        </StyledSpan>
                                    </FlexContainer>
                                }
                                onChange={(value: string | number | number[] | boolean) => {
                                    handleInputChange(value.toString(), LEVERAGE.LONG);
                                }}
                            />
                        </FlexContainer>
                        <Range
                            name="long"
                            isShowLabel={false}
                            value={longSliderValue}
                            marks={longLeverageMarks}
                            isShowMarks
                            min={1}
                            max={7}
                            step={0.1}
                            onChange={(value, name = '') => {
                                handleSliderChange(Number(value), name);
                            }}
                            // disabled={isLongLeverageDisabled}
                        />
                        {Number(longLeverage) >= 20 && (
                            <InfoMessage>{t('leverage_high_warning')}</InfoMessage>
                        )}
                        {isLongLeverageDisabled && (
                            <InfoMessage>{t('leverage_warning')}</InfoMessage>
                        )}
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            gap: 16,
                            flexDirection: 'column',
                        }}
                    >
                        <FlexContainer
                            styles={{
                                flexDirection: 'column',
                                width: '100%',
                                alignItems: 'flex-start',
                                gap: 8,
                            }}
                        >
                            <LabelText>{t('leverage_side', { side: t(LEVERAGE.SHORT) })}</LabelText>
                            <StyledInput
                                name="short"
                                hasTooltip={false}
                                value={shortLeverage}
                                // disabled={isShortLeverageDisabled}
                                fullWidth
                                error={errors.short}
                                rightElement={
                                    <FlexContainer
                                        className="value-up-down"
                                        styles={{
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '39px',
                                        }}
                                    >
                                        <StyledSpan
                                            disabled={isShortLeverageDisabled}
                                            onClick={() => {
                                                // !isShortLeverageDisabled &&
                                                handleIncrementDecrementValue(
                                                    LEVERAGE.SHORT,
                                                    -shortSliderValue,
                                                );
                                            }}
                                        >
                                            -
                                        </StyledSpan>
                                        <StyledSpan color={colors.text.tertiary}>|</StyledSpan>
                                        <StyledSpan
                                            disabled={isShortLeverageDisabled}
                                            onClick={() => {
                                                // !isShortLeverageDisabled &&
                                                handleIncrementDecrementValue(
                                                    LEVERAGE.SHORT,
                                                    shortSliderValue,
                                                );
                                            }}
                                        >
                                            +
                                        </StyledSpan>
                                    </FlexContainer>
                                }
                                onChange={(value: string | number | number[] | boolean) => {
                                    handleInputChange(value.toString(), LEVERAGE.SHORT);
                                }}
                            />
                        </FlexContainer>
                        <Range
                            name="short"
                            isShowLabel={false}
                            value={shortSliderValue}
                            min={1}
                            max={7}
                            marks={shortLeverageMarks}
                            isShowMarks
                            step={0.1}
                            onChange={(value, name = '') => {
                                handleSliderChange(Number(value), name);
                            }}
                            // disabled={isShortLeverageDisabled}
                        />
                        {Number(shortLeverage) >= 20 && (
                            <InfoMessage>{t('leverage_high_warning')}</InfoMessage>
                        )}
                        {isShortLeverageDisabled && (
                            <InfoMessage>{t('leverage_warning')}</InfoMessage>
                        )}
                    </FlexContainer>
                </FlexContainer>
            </ModalContent>
            <ModalActions>
                <Button
                    fullWidth
                    loading={false}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    {t('confirm')}
                </Button>
                <Button fullWidth variant="outlined" onClick={handleClose}>
                    {t('cancel')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { LeverageModal };
